import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RouteProp, useRoute, useNavigation } from '@react-navigation/native';

import { selectSelectedItem } from '@reducers/machines';
import { backendCall } from '@reducers/utils';
import { Machine } from '@interfaces/machines';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { MACHINE_FILTER_PARAM } from '@components/operatorPanel/constants/operatorPanel.constants';
import { MachineFilterType, MachineFilterOption, MachineFilterRouteProp, NavigationWithSetParams } from '@components/operatorPanel/interfaces';

const isValidFilterType = (value: unknown): value is MachineFilterType => {
  return value === 'current' || value === 'similar' || value === 'all';
};

export const useMachineFilter = () => {
  const route = useRoute<MachineFilterRouteProp>();
  const navigation = useNavigation<NavigationWithSetParams>();
  const dispatch = useAppDispatch();

  // set default filter to 'current' from url params
  const initialFilter = isValidFilterType(route.params?.[MACHINE_FILTER_PARAM]) ? route.params[MACHINE_FILTER_PARAM] : 'current';

  const [selectedFilter, setSelectedFilterState] = useState<MachineFilterType>(initialFilter);

  // url params updater
  const setSelectedFilter = useCallback(
    (filter: MachineFilterType) => {
      setSelectedFilterState(filter);
      navigation.setParams({ [MACHINE_FILTER_PARAM]: filter });
    },
    [navigation]
  );

  const selectedItem: Machine | null = useSelector(selectSelectedItem);
  const currentMachineId = selectedItem?.machineId;
  const currentMachineName = selectedItem?.fullName || selectedItem?.shortName || '';

  const [currentProductionOperationIds, setCurrentProductionOperationIds] = useState<number[]>([]);
  const [similarProductionOperationIds, setSimilarProductionOperationIds] = useState<number[]>([]);
  const [isCurrentFilterLoading, setIsCurrentFilterLoading] = useState(true);
  const [isSimilarFilterLoading, setIsSimilarFilterLoading] = useState(false);
  const baseOperationsFilter = "Status ne 'Finished'";

  const currentOperationsFilter = useMemo(() => {
    if (!currentMachineId) return undefined;

    return `${baseOperationsFilter} and Machines/any(machine: (machine/MachineId eq ${currentMachineId}) and (machine/LinkType eq 'Preferred' or machine/LinkType eq 'ProducedOn'))`;
  }, [currentMachineId, baseOperationsFilter]);

  const similarOperationsFilter = useMemo(() => {
    if (!selectedItem?.operations?.length) return undefined;

    const operationsFilter = selectedItem.operations
      .map((operation) => operation.operationId)
      .filter((operationId): operationId is number => typeof operationId === 'number')
      .map((operationId) => `OperationId eq ${operationId}`)
      .join(' or ');

    if (!operationsFilter) {
      return undefined;
    }

    return `${baseOperationsFilter} and (${operationsFilter})`;
  }, [selectedItem?.operations, baseOperationsFilter]);

  const buildLinksFilter = useCallback((ids: number[]) => {
    if (!ids.length) return undefined;

    return `Links/any(link: ${ids.map((id) => `link/ProductionOperationId eq ${id}`).join(' or ')})`;
  }, []);

  const currentLinksFilter = useMemo(() => buildLinksFilter(currentProductionOperationIds), [buildLinksFilter, currentProductionOperationIds]);
  const similarLinksFilter = useMemo(() => buildLinksFilter(similarProductionOperationIds), [buildLinksFilter, similarProductionOperationIds]);

  const fetchProductionOperationIds = useCallback(
    async (oDataFilter?: string, signal?: AbortSignal) => {
      if (!oDataFilter) return [];

      const ids = new Set<number>();
      const pageSize = 500;
      let skip = 0;

      while (true) {
        const response = await dispatch(
          backendCall({
            method: (api) => api.search.operationsSearch,
            params: {
              search: '',
              top: pageSize,
              skip,
              oDataFilter,
              select: ['ProductionOperationId'],
            },
            signal,
          })
        );

        const results = response?.searchResult ?? [];
        results.forEach((item) => {
          if (item?.productionOperationId) {
            ids.add(item.productionOperationId);
          }
        });

        const total = response?.totalResultCount ?? results.length;
        const hasMore = results.length > 0 && skip + results.length < total;
        if (!hasMore) {
          break;
        }

        skip += results.length;
      }

      return Array.from(ids);
    },
    [dispatch]
  );

  const currentFetchId = useRef(0);
  useEffect(() => {
    if (!currentOperationsFilter) {
      setCurrentProductionOperationIds([]);
      setIsCurrentFilterLoading(false);
      return;
    }

    setIsCurrentFilterLoading(true);
    const abortController = new AbortController();
    const fetchId = ++currentFetchId.current;

    (async () => {
      try {
        const ids = await fetchProductionOperationIds(currentOperationsFilter, abortController.signal);
        if (currentFetchId.current === fetchId) {
          setCurrentProductionOperationIds(ids);
          setIsCurrentFilterLoading(false);
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          setCurrentProductionOperationIds([]);
          setIsCurrentFilterLoading(false);
        }
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [currentOperationsFilter, fetchProductionOperationIds]);

  const similarFetchId = useRef(0);
  useEffect(() => {
    if (selectedFilter !== 'similar') {
      return;
    }

    if (!similarOperationsFilter) {
      setSimilarProductionOperationIds([]);
      setIsSimilarFilterLoading(false);
      return;
    }

    setIsSimilarFilterLoading(true);
    const abortController = new AbortController();
    const fetchId = ++similarFetchId.current;

    (async () => {
      try {
        const ids = await fetchProductionOperationIds(similarOperationsFilter, abortController.signal);
        if (similarFetchId.current === fetchId) {
          setSimilarProductionOperationIds(ids);
          setIsSimilarFilterLoading(false);
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          setSimilarProductionOperationIds([]);
          setIsSimilarFilterLoading(false);
        }
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [selectedFilter, similarOperationsFilter, fetchProductionOperationIds]);

  const emptyFilter = '1 eq 0';

  // Build OData filter for Batches (uses MachineId)
  const machineFilter = useMemo(() => {
    if (selectedFilter === 'all') {
      return undefined;
    }

    if (!currentLinksFilter && selectedFilter === 'current') {
      return emptyFilter;
    }

    if (selectedFilter === 'current') {
      if (!currentMachineId) {
        return emptyFilter;
      }

      return `MachineId eq ${currentMachineId} and (${currentLinksFilter})`;
    }

    if (selectedFilter === 'similar') {
      return similarLinksFilter ?? emptyFilter;
    }

    return undefined;
  }, [selectedFilter, currentMachineId, currentLinksFilter, similarLinksFilter]);

  // Build OData filter for NC Files
  const ncFileMachineFilter = useMemo(() => {
    if (selectedFilter === 'all') {
      return undefined;
    }

    if (selectedFilter === 'current') {
      return currentLinksFilter ?? emptyFilter;
    }

    if (selectedFilter === 'similar') {
      return similarLinksFilter ?? emptyFilter;
    }

    return emptyFilter;
  }, [selectedFilter, currentLinksFilter, similarLinksFilter]);

  // Filter options for UI
  const filterOptions: MachineFilterOption[] = useMemo(
    () => [
      { id: 'current', name: currentMachineName || 'Current Machine' },
      { id: 'similar', name: 'Similar Machines' },
      { id: 'all', name: 'All' },
    ],
    [currentMachineName]
  );

  // When true, the active filter resolves to zero results — consumers should skip the API call
  const isEmptyFilter = useMemo(() => {
    if (selectedFilter === 'all') return false;
    if (selectedFilter === 'current') return !currentLinksFilter;
    if (selectedFilter === 'similar') return !similarLinksFilter;
    return false;
  }, [selectedFilter, currentLinksFilter, similarLinksFilter]);

  // Filter is ready when the required data has been loaded
  const isFilterReady = useMemo(() => {
    if (selectedFilter === 'all') {
      return true;
    }
    if (selectedFilter === 'current') {
      return !isCurrentFilterLoading;
    }
    if (selectedFilter === 'similar') {
      return !isSimilarFilterLoading;
    }
    return true;
  }, [selectedFilter, isCurrentFilterLoading, isSimilarFilterLoading]);

  return {
    selectedFilter,
    setSelectedFilter,
    machineFilter,
    ncFileMachineFilter,
    currentMachineName,
    filterOptions,
    isFilterReady,
    isEmptyFilter,
    hasSimilarMachines: similarProductionOperationIds.length > 0,
  };
};
